import { Fragment, useState, useRef, useId, useCallback, MouseEvent, ChangeEvent, FC } from "react";
import { Button, Menu, MenuItem as MuiMenuItem, Popover, Tooltip } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";

import "./style.scss";
import { ReactComponent as CheckboxIcon } from "../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../assets/checkbox-checked.icon.svg";
import { MenuItem, SectionItem } from "../../common/interfaces/comps/menu-item.interface";
import { ImageCheckbox } from "../checkbox/checkbox.comp";

export enum DropdownMenuType {
    default,
    wider
}

export interface Action {
    icon?: FC<React.SVGProps<SVGSVGElement>>;
    name: string;
    action: () => void;
    // Shown as a tooltip on the menu entry — what the action will actually do
    // (DESIGN_GUIDE §4 list-action standard).
    description?: string;
}

export const ActionMenu = ({
    label,
    actions,
    disabled = false,
    rightPosition = false,
    minWidth,
    dropdownMenuType = DropdownMenuType.default
}: {
    label: string;
    actions: Action[];
    disabled?: boolean;
    rightPosition?: boolean;
    minWidth?: number;
    dropdownMenuType?: DropdownMenuType;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const triggerId = useId();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuItem = (action: () => void) => {
        action();
        handleMenuClose();
    };

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                endIcon={
                    <SvgIcon
                        component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                disabled={disabled}
                id={triggerId}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={handleMenuClick}
            >
                <span className="menu-button__label">{label}</span>
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": triggerId
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: `dropdown-menu${dropdownMenuType === DropdownMenuType.wider ? " dropdown-menu--wider" : ""}`,
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {actions.map(action => {
                    const item = (
                        <MuiMenuItem
                            key={action.name}
                            className="dropdown-menu-item"
                            onClick={() => handleMenuItem(action.action)}
                        >
                            {action.icon && (
                                <SvgIcon
                                    className="dropdown-menu-item__icon"
                                    component={action.icon}
                                    inheritViewBox
                                />
                            )}
                            <span className="dropdown-menu-item__text">{action.name}</span>
                        </MuiMenuItem>
                    );
                    return action.description ? (
                        <Tooltip
                            key={action.name}
                            title={action.description}
                            placement="right"
                            enterDelay={300}
                            // describeChild: the tooltip is a description
                            // (aria-describedby), never the accessible NAME —
                            // without it MUI sets aria-label={title} and a
                            // screen reader hears the description instead of
                            // the action.
                            describeChild
                        >
                            {item}
                        </Tooltip>
                    ) : (
                        item
                    );
                })}
            </Menu>
        </Fragment>
    );
};

export const CheckboxMenuFilter = ({
    label,
    list,
    checkedList,
    setCheckedList,
    showCounter = false,
    rightPosition = false,
    minWidth
}: {
    label: string;
    list: string[];
    checkedList: string[];
    setCheckedList: (arg1: string[]) => void;
    showCounter?: boolean;
    rightPosition?: boolean;
    minWidth?: number;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const triggerId = useId();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleChangeCheckedItem = useCallback(
        (item: string) => {
            const newList = [...checkedList];
            const index = newList.indexOf(item);
            index !== -1 ? newList.splice(index, 1) : newList.push(item);
            setCheckedList(newList);
        },
        [checkedList, setCheckedList]
    );

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                endIcon={
                    <SvgIcon
                        component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                id={triggerId}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={handleMenuClick}
            >
                <span className="menu-button__label">{label}</span>
                {showCounter && checkedList.length > 0 && (
                    <div className="menu-button__counter">{checkedList.length}</div>
                )}
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": triggerId
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {list.map(item => (
                    <div key={item} className="dropdown-menu-item--checkbox">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checkedList.includes(item)}
                                    onChange={() => handleChangeCheckedItem(item)}
                                    icon={<CheckboxIcon />}
                                    checkedIcon={<CheckboxCheckedIcon />}
                                />
                            }
                            label={item}
                        />
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

export const CheckboxMenuFilterT = <T,>({
    label,
    list,
    checkedList,
    setCheckedList,
    showCounter = false,
    rightPosition = false,
    minWidth,
    onClear
}: {
    label: string;
    list: MenuItem<T>[];
    checkedList: MenuItem<T>[];
    setCheckedList: (arg1: MenuItem<T>[]) => void;
    showCounter?: boolean;
    rightPosition?: boolean;
    minWidth?: number;
    onClear?: () => void;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const triggerId = useId();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    // Restore focus to the trigger ourselves (Popover has disableRestoreFocus):
    // after a portaled popover unmounts, MUI's built-in restore doesn't move
    // VoiceOver's reading cursor with it, leaving the cursor stranded on the
    // removed content so arrow navigation is dead. Re-focusing the trigger on
    // the next frame fires a fresh focus event VO follows (WCAG 2.4.3).
    const handleMenuClose = () => {
        const trigger = menuAnchor as HTMLElement | null;
        setMenuAnchor(null);
        requestAnimationFrame(() => trigger?.focus());
    };

    // Move focus into the checkbox group when the popover opens (MUI Popover,
    // unlike Menu, does not auto-focus its content) so keyboard/VoiceOver users
    // land on the first option instead of being left on the trigger (WCAG 2.4.3).
    const groupRef = useRef<HTMLDivElement>(null);
    const focusFirstOption = () => {
        groupRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    };

    const handleChangeCheckedItem = useCallback(
        (item: MenuItem<T>) => {
            const newList = [...checkedList];
            const index = newList.indexOf(item);
            index !== -1 ? newList.splice(index, 1) : newList.push(item);
            setCheckedList(newList);
        },
        [checkedList, setCheckedList]
    );

    const handleClear = (event: MouseEvent) => {
        event.stopPropagation();
        if (onClear) onClear();
    };

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                endIcon={
                    onClear && checkedList.length > 0 ? (
                        <SvgIcon
                            component={CloseIcon}
                            className="menu-button__clear"
                            inheritViewBox
                            role="button"
                            tabIndex={0}
                            aria-label="Clear selection"
                            onClick={handleClear}
                            onKeyDown={event => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (onClear) onClear();
                                }
                            }}
                        />
                    ) : (
                        <SvgIcon
                            component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                            inheritViewBox
                        />
                    )
                }
                id={triggerId}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={handleMenuClick}
            >
                <span className="menu-button__label">{label}</span>
                {showCounter && checkedList.length > 0 && (
                    <div className="menu-button__counter">{checkedList.length}</div>
                )}
            </Button>
            <Popover
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                disableRestoreFocus
                TransitionProps={{ onEntered: focusFirstOption }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        "aria-labelledby": triggerId,
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                <div role="group" aria-labelledby={triggerId} ref={groupRef}>
                    {list.map(item => (
                        <div key={item.label} className="dropdown-menu-item--checkbox">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkedList.includes(item)}
                                        onChange={() => handleChangeCheckedItem(item)}
                                        icon={<CheckboxIcon />}
                                        checkedIcon={<CheckboxCheckedIcon />}
                                    />
                                }
                                label={item.label}
                            />
                        </div>
                    ))}
                </div>
            </Popover>
        </Fragment>
    );
};

export const MultipleCheckboxMenuFilter = <T,>({
    label,
    multipleList,
    checkedList,
    setCheckedList,
    startIcon,
    showCounter = false,
    rightPosition = false,
    minWidth
}: {
    label: string;
    multipleList: SectionItem<T>[];
    checkedList: T[];
    setCheckedList: (arg1: T[]) => void;
    startIcon: FC<React.SVGProps<SVGSVGElement>>;
    showCounter?: boolean;
    rightPosition?: boolean;
    minWidth?: number;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const triggerId = useId();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);
    const [searchString, setSearchString] = useState<string>("");

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setSearchString(val ?? "");
    };

    const handleChangeCheckedItem = useCallback(
        (item: MenuItem<T>) => {
            const newList = [...checkedList];
            const index = newList.indexOf(item.value);
            index !== -1 ? newList.splice(index, 1) : newList.push(item.value);
            setCheckedList(newList);
        },
        [checkedList, setCheckedList]
    );

    const handleSelectUnselectAll = useCallback(
        (selectAll: boolean) => {
            if (selectAll) {
                const newList = [...checkedList];
                multipleList.forEach(sectionItem => {
                    sectionItem.list.forEach(item => {
                        const index = newList.indexOf(item.value);
                        if (index === -1) {
                            newList.push(item.value);
                        }
                    });
                });
                setCheckedList(newList);
            } else {
                setCheckedList([]);
            }
        },
        [multipleList, checkedList, setCheckedList]
    );

    const isAllSelected = useCallback(() => {
        return !multipleList.find(sectionItem =>
            sectionItem.list.find(item => !checkedList.includes(item.value))
        );
    }, [multipleList, checkedList]);

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                startIcon={<SvgIcon component={startIcon} inheritViewBox />}
                endIcon={
                    <SvgIcon
                        component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                id={triggerId}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={handleMenuClick}
            >
                <span className="menu-button__label">{label}</span>
                {showCounter && checkedList.length > 0 && (
                    <div className="menu-button__counter">{checkedList.length}</div>
                )}
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": triggerId
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                <div className="dropdown-menu-search">
                    <input
                        type="text"
                        placeholder="Search"
                        aria-label="Search"
                        onChange={handleSearchChange}
                        value={searchString}
                    />
                </div>
                <div className="dropdown-menu-item--checkbox">
                    <FormControlLabel
                        control={
                            <ImageCheckbox
                                checked={isAllSelected()}
                                ariaLabel="Unselect / Select All"
                            />
                        }
                        onClick={() => handleSelectUnselectAll(!isAllSelected())}
                        label={"Unselect / Select All"}
                    />
                </div>
                {multipleList.map(listItem => (
                    <div key={crypto.randomUUID()} className="dropdown-menu-group">
                        {listItem.list.filter(item =>
                            item.label
                                .toLocaleLowerCase()
                                .includes(searchString.toLocaleLowerCase())
                        ).length > 0 && (
                            <div className="dropdown-menu-section">{listItem.title}</div>
                        )}
                        {listItem.list
                            .filter(item =>
                                item.label
                                    .toLocaleLowerCase()
                                    .includes(searchString.toLocaleLowerCase())
                            )
                            .map(item => (
                                <div
                                    key={crypto.randomUUID()}
                                    className="dropdown-menu-item--checkbox"
                                >
                                    <FormControlLabel
                                        control={
                                            <ImageCheckbox
                                                checked={checkedList.includes(item.value)}
                                                ariaLabel={item.label}
                                            />
                                        }
                                        onClick={() => handleChangeCheckedItem(item)}
                                        label={item.label}
                                    />
                                </div>
                            ))}
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

export const SelectionFilter = ({
    list,
    selectedItem,
    setSelectedItem,
    placeholder = "- Select -",
    rightPosition = false,
    disabled = false,
    minWidth,
    dropdownMenuType = DropdownMenuType.default,
    className = "",
    ariaLabelledBy
}: {
    list: string[];
    selectedItem?: string;
    setSelectedItem: (arg1: string) => void;
    placeholder?: string;
    rightPosition?: boolean;
    disabled?: boolean;
    minWidth?: number;
    dropdownMenuType?: DropdownMenuType;
    className?: string;
    ariaLabelledBy?: string;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const triggerId = useId();
    const [selectAnchor, setSelectAnchor] = useState<Element | null>(null);
    const selectOpen = Boolean(selectAnchor);

    const handleSelectClick = (event: MouseEvent<HTMLElement>) => {
        setSelectAnchor(event.currentTarget);
    };

    const handleSelectClose = () => {
        setSelectAnchor(null);
    };

    const handleSelectItem = (item: string) => {
        setSelectedItem(item);
        handleSelectClose();
    };

    return (
        <Fragment>
            <Button
                className={`menu-button ${className}`}
                variant="text"
                disabled={disabled}
                endIcon={
                    <SvgIcon
                        component={selectOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                id={triggerId}
                aria-haspopup="true"
                aria-expanded={selectOpen}
                aria-labelledby={ariaLabelledBy ? `${ariaLabelledBy} ${triggerId}` : undefined}
                onClick={handleSelectClick}
            >
                <span className="menu-button__label">{selectedItem ?? placeholder}</span>
            </Button>
            <Menu
                anchorEl={selectAnchor}
                open={selectOpen}
                onClose={handleSelectClose}
                MenuListProps={{
                    "aria-labelledby": triggerId
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: `dropdown-menu${dropdownMenuType === DropdownMenuType.wider ? " dropdown-menu--wider" : ""}`,
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {list.map(item => (
                    <MuiMenuItem
                        key={item}
                        className={`dropdown-menu-item${item === selectedItem ? " dropdown-menu-item--selected" : ""}`}
                        aria-current={item === selectedItem ? "true" : undefined}
                        onClick={() => handleSelectItem(item)}
                    >
                        <span className="dropdown-menu-item__text">{item}</span>
                    </MuiMenuItem>
                ))}
            </Menu>
        </Fragment>
    );
};

export const SelectionFilterT = <T,>({
    list,
    selectedItem,
    setSelectedItem,
    rightPosition = false,
    disabled = false,
    type = DropdownMenuType.default,
    minWidth,
    startIcon
}: {
    list: MenuItem<T>[];
    selectedItem: MenuItem<T>;
    setSelectedItem: (arg1: MenuItem<T>) => void;
    rightPosition?: boolean;
    disabled?: boolean;
    type?: DropdownMenuType;
    minWidth?: number;
    startIcon?: FC<React.SVGProps<SVGSVGElement>>;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const triggerId = useId();
    const [selectAnchor, setSelectAnchor] = useState<Element | null>(null);
    const selectOpen = Boolean(selectAnchor);

    const handleSelectClick = (event: MouseEvent<HTMLElement>) => {
        setSelectAnchor(event.currentTarget);
    };

    const handleSelectClose = () => {
        setSelectAnchor(null);
    };

    const handleSelectItem = (item: MenuItem<T>) => {
        setSelectedItem(item);
        handleSelectClose();
    };

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                disabled={disabled}
                startIcon={startIcon ? <SvgIcon component={startIcon} inheritViewBox /> : undefined}
                endIcon={
                    <SvgIcon
                        component={selectOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                id={triggerId}
                aria-haspopup="true"
                aria-expanded={selectOpen}
                onClick={handleSelectClick}
            >
                <span className="menu-button__label">{selectedItem.label}</span>
            </Button>
            <Menu
                anchorEl={selectAnchor}
                open={selectOpen}
                onClose={handleSelectClose}
                MenuListProps={{
                    "aria-labelledby": triggerId
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: `dropdown-menu${type === DropdownMenuType.wider ? " dropdown-menu--wider" : ""}`,
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {list.map(item => (
                    <MuiMenuItem
                        key={crypto.randomUUID()}
                        className={`dropdown-menu-item${item.value === selectedItem.value ? " dropdown-menu-item--selected" : ""}`}
                        aria-current={item.value === selectedItem.value ? "true" : undefined}
                        onClick={() => handleSelectItem(item)}
                    >
                        {item.icon && (
                            <SvgIcon
                                className="dropdown-menu-item__icon"
                                component={item.icon}
                                inheritViewBox
                            />
                        )}
                        <span className="dropdown-menu-item__text">{item.label}</span>
                    </MuiMenuItem>
                ))}
            </Menu>
        </Fragment>
    );
};
